// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

/**
 * Escalation: the cost of time passing.
 *
 * The contingency panel in this product already tells the user that
 * escalation is not contingency -- that it is "a known cost with an unknown
 * amount", and that folding it into the reserve means the reserve is spent
 * before the first unknown arrives. It then gave them nowhere to put it, so
 * anyone who agreed had two options: bury it in a line item where no one can
 * see the rate, or do the exact thing the panel warns against.
 *
 * This is the somewhere.
 *
 * Three decisions, so the number is defensible rather than merely present:
 *
 *   1. It is priced to the MIDPOINT of construction, not to the start and not
 *      to the end. Half the work is bought before the midpoint and half after,
 *      so the midpoint is where the average dollar is spent. Pricing to the
 *      start under-recovers on a long job; pricing to the end over-recovers on
 *      every job.
 *
 *   2. It COMPOUNDS. A 4% annual rate over eighteen months is 6.06%, not 6%.
 *      The difference is small and the arithmetic is standard; using simple
 *      interest here would be a quiet, permanent under-recovery that gets
 *      worse the further out the job sits.
 *
 *   3. It is a COST OF WORK, so overhead and profit are charged on it and
 *      contingency sized against direct cost includes it. An escalated dollar
 *      is a dollar you have to spend; it carries fee like any other.
 *
 * The user types one number -- the annual rate. Everything else is read from
 * the schedule and shown.
 */

export interface EscalationSettings {
  /** Annual escalation rate as a percentage. 0 means not priced. */
  annualPct: number;
  /**
   * Months from today to the construction midpoint, when the user has
   * overridden it. Null means derive it from the schedule, which is the
   * normal case and the one that stays correct when the schedule moves.
   */
  monthsOverride: number | null;
}

export const DEFAULT_ESCALATION: EscalationSettings = { annualPct: 0, monthsOverride: null };

/** Whether this estimate prices escalation at all. */
export const escalationPriced = (e: EscalationSettings | undefined | null) =>
  !!e && e.annualPct > 0;

const MS_PER_DAY = 86_400_000;
const DAYS_PER_MONTH = 365.25 / 12;

/**
 * Whole-ish months between two dates, fractional.
 *
 * Calendar months would be more precise and are not worth it: the input is an
 * annual percentage somebody estimated, and a two-day difference in the
 * midpoint cannot matter to the answer.
 */
export function monthsBetween(from: Date, to: Date): number {
  const a = from?.getTime?.(), b = to?.getTime?.();
  if (!Number.isFinite(a!) || !Number.isFinite(b!)) return 0;
  return (b! - a!) / MS_PER_DAY / DAYS_PER_MONTH;
}

/**
 * Months from today to the midpoint of construction.
 *
 * That is the wait until the job starts, plus half of how long it runs. A job
 * starting in six months and running two years has its midpoint eighteen
 * months out, and escalation is priced on eighteen months rather than on six
 * or on thirty.
 *
 * A start date already in the past contributes nothing rather than
 * subtracting: the work still ahead is what escalates.
 */
export function monthsToMidpoint(
  startDate: string | null | undefined,
  durationMonths: number,
  today: Date = new Date(),
): number {
  const dur = Number.isFinite(durationMonths) && durationMonths > 0 ? durationMonths : 0;
  let untilStart = 0;
  if (startDate) {
    const start = new Date(startDate);
    if (Number.isFinite(start.getTime())) untilStart = Math.max(0, monthsBetween(today, start));
  }
  return untilStart + dur / 2;
}

/**
 * The compounded rate over a span, as a percentage.
 *
 * (1 + annual) ^ (months / 12) − 1
 */
export function effectivePct(annualPct: number, months: number): number {
  const r = Number.isFinite(annualPct) ? annualPct / 100 : 0;
  const m = Number.isFinite(months) && months > 0 ? months : 0;
  if (r === 0 || m === 0) return 0;
  // A rate at or below −100%/yr has no meaningful compounding; clamp rather
  // than returning NaN from a fractional power of a negative base.
  if (r <= -1) return -100;
  return (Math.pow(1 + r, m / 12) - 1) * 100;
}

export interface EscalationResult {
  /** Months used, whether derived or overridden. */
  months: number;
  /** Compounded rate actually applied. */
  pct: number;
  /** Dollars added to the cost of work. */
  amount: number;
  /** False when the user has typed a midpoint of their own. */
  derived: boolean;
}

/**
 * What this estimate's escalation comes to.
 *
 * `directCost` is the direct cost subtotal: escalation is a percentage of the
 * work, not of the marked-up total. Overhead and profit are then charged on
 * the result, which is handled in computeFinal rather than here.
 */
export function computeEscalation(
  directCost: number,
  settings: EscalationSettings | null | undefined,
  startDate: string | null | undefined,
  durationMonths: number,
  today: Date = new Date(),
): EscalationResult {
  const s = settings || DEFAULT_ESCALATION;
  const derived = s.monthsOverride == null;
  const months = derived
    ? monthsToMidpoint(startDate, durationMonths, today)
    : Math.max(0, s.monthsOverride!);
  const pct = effectivePct(s.annualPct, months);
  const base = Number.isFinite(directCost) ? directCost : 0;
  return { months, pct, amount: base * (pct / 100), derived };
}

/** Months as an estimator would say them: "18 mo", "2 mo". */
export const fmtMonths = (m: number) =>
  `${m >= 10 ? Math.round(m) : Math.round(m * 10) / 10} mo`;
